import { useState } from 'react';
import { isAxiosError } from 'axios';
import { RichTextEditor } from '@/components/RichTextEditor';
import { CustomDatePicker } from '@/components/form/CustomDatePicker';
import { MediaField } from './MediaField';
import { uploadAdMedia, updateAdMedia, type AdMedia, type AdRow, type SaveAdPayload } from '@/api/ads';

/**
 * Форма объявления — поля 1:1 старый Orchid AdEditLayout: название, текст
 * (Quill → RichTextEditor), один медиафайл, даты начала/завершения и
 * чекбокс включения. Проверки дат повторяют AdEditScreen::save(), но сервер
 * всё равно проверяет их заново — он источник истины.
 */
export function AdForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: AdRow;
  onSave: (payload: SaveAdPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [dateFrom, setDateFrom] = useState(initial?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(initial?.dateTo ?? '');
  const [active, setActive] = useState(initial?.active ?? false);
  const [media, setMedia] = useState<AdMedia | null>(initial?.media ?? null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  const submit = async () => {
    // Обе даты обязательны: бессрочных объявлений нет (в отличие от старого
    // Orchid, где даты можно было не заполнять) — см. докблок AdController.
    const missing: string[] = [];
    if (!name.trim()) missing.push('name');
    if (!dateFrom) missing.push('dateFrom');
    if (!dateTo) missing.push('dateTo');

    let message: string | null = null;
    if (missing.length > 0) message = 'Заполните обязательные поля.';
    else if (dateFrom && dateTo && dateFrom > dateTo) {
      missing.push('dateFrom');
      message = 'Дата начала не может быть позже даты завершения.';
    } else if (dateTo && dateTo < new Date().toISOString().slice(0, 10)) {
      missing.push('dateTo');
      message = 'Дата завершения не может быть меньше текущей даты.';
    }

    setFieldErrors(missing);
    if (message) {
      setError(message);
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        active,
        mediaId: media?.id ?? null,
      });
    } catch (err) {
      const detail = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(detail || 'Не удалось сохранить объявление.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`operator-template-form${saving ? ' operator-template-form--saving' : ''}`} aria-busy={saving}>
      {error && <p className="field-error">{error}</p>}

      <div className="edit-fields">
        <label className={`edit-field${invalid('name')}`}>
          <span>Название *</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
        </label>

        <div className="edit-field" />

        {/* clearable={false} — у объявления период показа обязателен, «бессрочно»
            бывает только у даты отключения кабинета клиента. */}
        <div className={`edit-field${invalid('dateFrom')}`}>
          <span>Дата начала *</span>
          <CustomDatePicker value={dateFrom} onChange={setDateFrom} clearable={false} />
        </div>

        <div className={`edit-field${invalid('dateTo')}`}>
          <span>Дата завершения *</span>
          <CustomDatePicker value={dateTo} onChange={setDateTo} clearable={false} />
        </div>

        <label className="edit-field edit-field--checkbox edit-field--full">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={saving}
          />
          <span>Включено</span>
        </label>
        <p className="edit-field__hint edit-field__hint--oneline">
          Отметьте, чтобы объявление показывалось в кабинетах клиентов в указанный период.
        </p>

        {/* Не <label>: он пересылает клик на первый labelable-потомок, а это кнопка тулбара (см. DataOperatorsPage). */}
        <div className="edit-field edit-field--full">
          <span id="ad-text-label">Текст объявления</span>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            disabled={saving}
            ariaLabelledBy="ad-text-label"
          />
        </div>

        <div className="edit-field edit-field--full">
          <span>Медиафайл</span>
          <MediaField
            media={media}
            onChange={setMedia}
            disabled={saving}
            onUpload={uploadAdMedia}
            onUpdateParams={updateAdMedia}
          />
        </div>
      </div>

      <div className="operator-template-form__actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
          Отмена
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
          Сохранить
        </button>
      </div>
    </div>
  );
}
